import { createAsyncThunk } from "@reduxjs/toolkit";
import { getProductsKroger, getProductDetailsKroger, getKrogerStoreLocation, getGuestProperty } from "../../utils/kroger";
import request from "../../utils/request";
import shuffle from 'lodash/shuffle';
import excludedProductIds from "../../constants/ExcludedProductIds";
import { setLoadingFromCache } from "../slices/guestSlices";

const krogerCache = (() => {
    let cache = null;
    let lastUpdated = null;

    return {
        get: () => cache,
        set: (data) => {
            cache = data;
            lastUpdated = Date.now();
        },
        isValid: () => {
            return lastUpdated !== null && Date.now() - lastUpdated < 30 * 60 * 1000; // 30 minutes in milliseconds
        },
    };
})();

export const getKrogerToken = async () => {
    if (krogerCache.isValid()) {
        // if the cache is still valid, return the cached data
        return krogerCache.get();
    } else {
        // otherwise, fetch the access token from /kroger/ and update the cache
        const { data } = await request.get("/kroger/");
        krogerCache.set(data);
        return krogerCache.get();
    }
}

const getProducts = createAsyncThunk(
    "products/getProducts",
    async (params, { rejectWithValue }) => {
        
        const company = localStorage.getItem('activeCompany');
        const companyData = JSON.parse(company);

        const term = params.term ? params.term : 'Food';
        const start = params?.start;
        const limit = params?.limit;

        try {
            let products, total;
            if (term) {
                let resp = await getProductsKroger(term, start, limit, companyData);
                console.log('kroger response => ', resp)
                products = resp.data;
                total = resp.meta?.pagination.total || 0;
            }
            return { products, total };

        } catch (err) {
            console.log(err.message);
            return rejectWithValue(err.response.data);
        }
    }
);

// Helper function to add delay between requests
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Helper function to fetch with retry and rate limiting
const fetchWithRetry = async (category, start, end, activeCompany, retries = 3, delayMs = 500) => {
    for (let i = 0; i < retries; i++) {
        try {
            return await getProductsKroger(category, start, end, activeCompany);
        } catch (err) {
            // If 429 (rate limit), wait longer before retry
            if (err.response?.status === 429) {
                const waitTime = delayMs * Math.pow(2, i); // Exponential backoff
                console.warn(`Rate limited for category ${category}, waiting ${waitTime}ms before retry ${i + 1}/${retries}`);
                await delay(waitTime);
                continue;
            }
            // For other errors, throw immediately
            throw err;
        }
    }
    throw new Error(`Failed to fetch category ${category} after ${retries} retries`);
};

const getMultipleCategoryProducts = createAsyncThunk(
    "productDetails/getMultipleCategoryProducts",
    async (params, { rejectWithValue, getState, dispatch }) => {
        const { customterms, start, end, oneCategory = false, activeCompany } = params;
        
        // Check cache first - if all categories are already loaded, return cached data
        const state = getState();
        const companyName = activeCompany?.name;
        
        // First check Redux state - if products already exist, return them immediately
        // BUT: Only for initial load (start === 0), NOT for pagination (start > 0)
        // Pagination always needs to fetch from API to get more products
        if (companyName && state.multipleCategoryProducts?.[companyName] && start === 0) {
            const companyState = state.multipleCategoryProducts[companyName];
            const allCached = customterms.every((category) => {
                const catState = companyState[category];
                // Check if we have products for this category and we're fetching from start (not pagination)
                // Also check that we're not currently loading (to avoid race conditions)
                return catState && 
                       catState.products && 
                       Array.isArray(catState.products) && 
                       catState.products.length > 0 && 
                       !catState.loading;
            });
            
            if (allCached) {
                // Return cached products from Redux state - no API call needed
                // This only happens for initial load (start === 0)
                const cachedProducts = customterms.map((category) => {
                    return companyState[category].products || [];
                });
                const cachedTotals = customterms.map((category) => {
                    return companyState[category].total || 0;
                });
                return { 
                    products: oneCategory ? cachedProducts[0] : cachedProducts, 
                    totals: oneCategory ? null : cachedTotals 
                };
            }
        }
        
        // For pagination (start > 0), always skip cache and fetch from API
        // This ensures we get the next page of products
        
        // Fallback: Check localStorage if Redux state is empty (async hydration)
        if (companyName && typeof window !== 'undefined' && start === 0) {
            try {
                // Check localStorage cache immediately (no delay for cache hits)
                const stored = localStorage.getItem("multipleCategoryProducts");
                if (stored) {
                    const parsed = JSON.parse(stored);
                    if (parsed?.[companyName]) {
                        const companyState = parsed[companyName];
                        const allCached = customterms.every((category) => {
                            const catState = companyState[category];
                            return catState && catState.products?.length > 0;
                        });
                        
                        if (allCached) {
                            // Return cached products immediately - no delay, no loading state
                            // This makes cache hits instant
                            const cachedProducts = customterms.map((category) => {
                                return companyState[category]?.products || [];
                            });
                            const cachedTotals = customterms.map((category) => {
                                return companyState[category]?.total || 0;
                            });
                            
                            return { 
                                products: oneCategory ? cachedProducts[0] : cachedProducts, 
                                totals: oneCategory ? null : cachedTotals 
                            };
                        }
                    }
                }
            } catch (err) {
                // Silently fail, will fetch from API
            }
        }
        
        try {
            // Fetching property Data From Local Storage
            const property = localStorage.getItem("property") && JSON.parse(localStorage.getItem("property"))
            if (property) {
                let products = null;
                let totals = null;
                
                if (oneCategory) {
                    // Single category - fetch directly
                    products = await fetchWithRetry(customterms[0], start, end, activeCompany);
                    products = products.data;
                    // For pagination: return null totals so reducer APPENDS products
                    // For initial load: return totals array so reducer REPLACES products
                    // The reducer logic: !action.payload.totals ? APPEND : REPLACE
                    return { 
                        products, 
                        totals: null  // null = append (pagination), array = replace (initial load)
                    };
                } else {
                    // Multiple categories - fetch sequentially with delay to avoid rate limiting
                    products = [];
                    totals = [];
                    
                    for (let i = 0; i < customterms.length; i++) {
                        const category = customterms[i];
                        try {
                            const result = await fetchWithRetry(category, start, end, activeCompany);
                            products.push(result.data);
                            totals.push(result.meta?.pagination.total || 0);
                            
                            // Add delay between requests (except for the last one)
                            if (i < customterms.length - 1) {
                                await delay(300); // 300ms delay between category requests
                            }
                        } catch (err) {
                            console.error(`Error fetching category ${category}:`, err);
                            // Continue with other categories even if one fails
                            products.push([]);
                            totals.push(0);
                        }
                    }
                    
                    return { products, totals };
                }
            }
        } catch (err) {
            console.log(err)
            if (err.request) {
                return rejectWithValue({ error: "No response from the server, please try again later" });
            }
            return rejectWithValue(err.response?.data || { error: err.message });
        }
    }
)

const getProductDetails = createAsyncThunk(
    "productDetails/getProductDetails",
    async (id, { rejectWithValue, getState }) => {
        // Check cache first to avoid duplicate API calls
        const state = getState();
        const cache = state.productDetails?.cache || {};
        
        // Check cache by request ID first
        if (cache[id]) {
            return cache[id];
        }
        
        // Also check cache by productId, _id, or upc in case the request ID differs
        for (const cachedProduct of Object.values(cache)) {
            const cachedId = cachedProduct?.productId || cachedProduct?._id || cachedProduct?.upc;
            if (cachedId === id) {
                return cachedProduct;
            }
        }
        
        // Fallback: Check localStorage if Redux cache is empty (async hydration)
        if (typeof window !== 'undefined') {
            try {
                const stored = localStorage.getItem("productDetailsCache");
                if (stored) {
                    const parsed = JSON.parse(stored);
                    // Check by request ID
                    if (parsed[id]) {
                        return parsed[id];
                    }
                    // Check by productId, _id, or upc
                    for (const cachedProduct of Object.values(parsed)) {
                        const cachedId = cachedProduct?.productId || cachedProduct?._id || cachedProduct?.upc;
                        if (cachedId === id) {
                            return cachedProduct;
                        }
                    }
                }
            } catch (err) {
                // Silently fail, will fetch from API
            }
        }

        try {
            // const data = await getKrogerToken()
            if (id.length === 24) {
                // Check if this product ID is excluded from MongoDB fetches
                if (excludedProductIds.includes(id)) {
                    return rejectWithValue({ 
                        error: "This product is no longer available from the database. Please use Kroger API." 
                    });
                }
                
                const company = localStorage.getItem('activeCompany');
                const companyData = JSON.parse(company);
                if (!companyData || companyData?.name == 'Ralphs') {
                    const { data: { data: product } } = await request.get(`/products/${id}`);
                    return product;
                } else if (companyData && companyData.name != 'Ralphs') {
                    const { data } = await request.get(`/company/products-details/${id}`)
                    return data;
                }
            } else {
                const product = getProductDetailsKroger(id);
                return product;
            }
        } catch (err) {
            if (err.request) {
                return rejectWithValue({ error: "No response from the server, please try again later" });
            }
            return rejectWithValue(err.response.data);
        }
    }
);

const getRelatedProducts = createAsyncThunk(
    "relatedProducts/getRelatedProducts",
    async (params, { rejectWithValue }) => {

        // destructuring function parameters
        const term = params?.term;
        const start = params?.start;
        const company = localStorage.getItem('activeCompany');
        const companyData = JSON.parse(company);

        try {
            // const data = await getKrogerToken()

            let products = await getProductsKroger(term, start, 10, companyData);
            products = products.data;

            return products;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

const getPackages = createAsyncThunk(
    "packages/getPackages",
    async (id, { rejectWithValue }) => {

        try {
            // getting saved packages from the DB
            const { data } = await request.get('/packages/active')

            return data.packages;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

const getOrderCompletedImages = createAsyncThunk(
    "OrderCompletedImages/getOrderCompletedImages",
    async (id, { rejectWithValue }) => {

        try {

            const { data } = await request.get(`/orders/images/${id}`);
            return data.signedUrls;

        } catch (err) {
            if (err.request) {
                return rejectWithValue({ error: "No response from the server, please try again later" });
            }
            return rejectWithValue(err.response.data);
        }
    }
);

const getGuestLocationId = createAsyncThunk(
    "GuestLocationId/getGuestLocationId",
    async (params, { rejectWithValue }) => {
        try {
            const id = await getGuestProperty(params);
            return id;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);


export { getProducts, getProductDetails, getRelatedProducts, getPackages, getMultipleCategoryProducts, getOrderCompletedImages, getGuestLocationId }
